/**
 * Detail pane for a pipeline step that has no view of its own.
 *
 * ONE generic component for every such step. It is driven entirely by the
 * catalog payload and contains NO step-specific logic — no routes, no
 * collections, no computation. It is the nav entry's detail pane, not an
 * implementation of the step: RETScreen Validation, Pulse Sync, SIRE and
 * EEVC are not built, and clicking their nav rows must say so rather than
 * doing nothing.
 *
 * Everything rendered here is read from the step payload. Nothing is derived.
 */
import { AlertTriangle, ArrowLeft, Check, Clock, RefreshCw } from "lucide-react";
import { useT } from "@/contexts/LanguageContext";
import {
  componentLabel, cycleProgressPercent, statusBadgeClass, stepIcon, stepLabel,
} from "@/utils/pipelineNav";

/** One prerequisite: its label, and whether it counts as satisfied yet. */
const PrerequisiteRow = ({ prereqKey, step, steps, t }) => {
  const target = (steps || []).find((s) => s.key === prereqKey) || { key: prereqKey };
  const detail = (step.unmet_prerequisite_details || []).find((d) => d.key === prereqKey);
  const satisfied = !detail;

  return (
    <div className="stub-prereq" data-testid={`stub-prereq-${prereqKey}`}>
      <span className={`stub-prereq-icon ${satisfied ? "met" : "unmet"}`}>
        {satisfied ? <Check size={14} /> : <Clock size={14} />}
      </span>
      <span className="stub-prereq-label">{stepLabel(t, target)}</span>
      <span className="stub-prereq-state">
        {satisfied
          ? t("pipeline_prereq_satisfied")
          // Phrased from the prerequisite step's own reason code, so Pulse Sync
          // reads as "has not completed a cycle yet".
          : t(`pipeline_reason_${detail.reason_code}`)}
      </span>
    </div>
  );
};

/**
 * The cycle record of a non-terminal step.
 *
 * Prorated months are listed, not summarized away: the spec requires every
 * scaled month to travel with the cycle record and be visible to any consumer
 * of the cycle's output.
 */
const CycleRecord = ({ step, t }) => {
  const cycle = step.cycle || {};
  const prorated = cycle.prorated_months || [];
  const stateLabel = step.cycle_state === "validated_and_training"
    ? t("pulse_state_validated_training")
    : t("pulse_state_accumulating");

  const monthText = (entry) => {
    if (entry && typeof entry === "object") {
      const month = entry.month || entry.period || "";
      const days = entry.days_covered ?? entry.days ?? null;
      return days === null ? month : `${month} — ${days} ${t("pulse_days_of_coverage")}`;
    }
    return String(entry);
  };

  return (
    <div className="card" data-testid="stub-cycle-record">
      <div className="card-header">
        <div className="card-title">{t("pulse_cycle_record")}</div>
      </div>

      <p className="text-muted stub-note">{t("pulse_never_completes_note")}</p>

      <div className="stub-cycle-stats">
        <div className="stub-cycle-stat">
          <div className="stub-cycle-stat-label">{t("pulse_cycles_complete")}</div>
          <div className="stub-cycle-stat-value" data-testid="stub-cycles-complete">
            <RefreshCw size={14} />
            {cycle.cycles_complete || 0}
          </div>
        </div>
        <div className="stub-cycle-stat">
          <div className="stub-cycle-stat-label">{t("pulse_current_cycle")}</div>
          <div className="stub-cycle-stat-value" data-testid="stub-current-cycle">
            {cycle.current_cycle || 1}
          </div>
        </div>
        <div className="stub-cycle-stat">
          <div className="stub-cycle-stat-label">{t("pulse_cycle_coverage")}</div>
          <div className="stub-cycle-stat-value" data-testid="stub-cycle-coverage">
            {cycle.months_covered || 0} / {cycle.months_required || 0} {t("pulse_months")}
          </div>
        </div>
        <div className="stub-cycle-stat">
          <div className="stub-cycle-stat-label">{t("pulse_state")}</div>
          <div className="stub-cycle-stat-value" data-testid="stub-cycle-state">{stateLabel}</div>
        </div>
      </div>

      <div className="progress-bar stub-cycle-bar">
        <div
          className="progress-fill pipeline-cycle-fill"
          style={{ width: `${cycleProgressPercent(cycle)}%` }}
        ></div>
      </div>

      <div className="stub-anomalies" data-testid="stub-cycle-anomalies">
        <div className="stub-anomalies-title">
          <AlertTriangle size={14} />
          {t("pulse_scaled_months")}
        </div>
        {prorated.length === 0 ? (
          <p className="text-muted stub-note">{t("pulse_no_scaled_months")}</p>
        ) : (
          <ul className="stub-anomaly-list">
            {prorated.map((entry, i) => (
              <li key={i} data-testid={`stub-prorated-month-${i}`}>
                {monthText(entry)} — {t("pulse_values_were_scaled")}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

const PipelineStepStubView = ({ step, parentStep, steps, onBack }) => {
  const t = useT();
  if (!step) return null;

  const Icon = stepIcon(parentStep ? parentStep.key : step.key);
  const label = stepLabel(t, step);
  const position = parentStep ? parentStep.order : step.order;
  // A parallel step is a container: its components carry their own built state,
  // so "not built" belongs to each of them and never to the parent. Step 10 has
  // one built component and one that is not.
  const isParallel = step.parallel === true && (step.components || []).length > 0;
  const notBuilt = step.implemented === false && !isParallel;
  const nonTerminal = step.terminal === false;
  const prerequisites = step.prerequisites || [];

  return (
    <div data-testid="pipeline-step-stub-view">
      <div className="page-header">
        <button className="btn btn-secondary stub-back" onClick={onBack} data-testid="stub-back">
          <ArrowLeft size={16} />
          {t("back")}
        </button>
        <div className="stub-heading">
          <span className="stub-heading-icon"><Icon size={22} /></span>
          <div>
            <h1 className="page-title" data-testid="stub-step-title">{label}</h1>
            <p className="page-subtitle">
              {t("pipeline_step_position")} {position}
              {parentStep && ` · ${t("pipeline_parallel_component_of")} ${stepLabel(t, parentStep)}`}
              {step.internal_name && ` · ${t("pipeline_internal_name")}: ${step.internal_name}`}
            </p>
          </div>
        </div>
      </div>

      {notBuilt && (
        <div className="card warning" data-testid="stub-not-built">
          <div className="card-title">{t("pipeline_not_built")}</div>
          <p className="stub-note">{t("pipeline_not_built_detail")}</p>
        </div>
      )}

      {nonTerminal && <CycleRecord step={step} t={t} />}

      {/* The parallel pair, as a pair. They occupy one position in the
          sequence and run alongside each other, so they are listed together
          and unnumbered. */}
      {isParallel && (
        <div className="card" data-testid="stub-parallel-components">
          <div className="card-header">
            <div className="card-title">{t("pipeline_runs_in_parallel")}</div>
          </div>
          <div className="stub-prereq-list">
            {(step.components || []).map((component) => (
              <div
                className="stub-prereq"
                key={component.key}
                data-testid={`stub-component-${component.key}`}
              >
                <span className="stub-prereq-icon met" aria-hidden="true">∥</span>
                <span className="stub-prereq-label">{componentLabel(t, component)}</span>
                <span className="stub-prereq-state">
                  {component.implemented === false
                    ? t("pipeline_not_built")
                    : t(`pipeline_status_${component.status || "not_started"}`)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="card" data-testid="stub-prerequisites">
        <div className="card-header">
          <div className="card-title">{t("pipeline_prerequisites")}</div>
          {!nonTerminal && (
            <span className={`badge ${statusBadgeClass(step.status) === "completed" ? "badge-success" : "badge-neutral"}`}>
              {t(`pipeline_status_${step.status || "not_started"}`)}
            </span>
          )}
        </div>
        {prerequisites.length === 0 ? (
          <p className="text-muted stub-note">{t("pipeline_no_prerequisites")}</p>
        ) : (
          <div className="stub-prereq-list">
            {prerequisites.map((key) => (
              <PrerequisiteRow key={key} prereqKey={key} step={step} steps={steps} t={t} />
            ))}
          </div>
        )}
        {/* Soft gating: prerequisites are reported, never enforced. */}
        <p className="text-muted stub-note">{t("pipeline_soft_gating_note")}</p>
      </div>
    </div>
  );
};

export default PipelineStepStubView;
